import { useCallback, useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users as UsersIcon, Calculator, Settings as SettingsIcon, LogOut, Square, Gift, CalendarDays, UserCog, Menu, X as XIcon, Package, TrendingUp, ShoppingCart, ShoppingBag, Wallet, Tags, BarChart3, Store, HardDrive } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { hasMenuAccess } from "../lib/menuAccess";

// menuKey: null => visible to everyone authenticated (currently only Dashboard, super_admin only)
const ALL_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard", superAdminOnly: true },
  { to: "/employees", label: "Karyawan", icon: UsersIcon, testId: "nav-employees", badgeKey: "contract_expiring", menuKey: "karyawan" },
  { to: "/payroll", label: "Payroll", icon: Calculator, testId: "nav-payroll", menuKey: "payroll" },
  { to: "/inventory", label: "Inventory", icon: Package, testId: "nav-inventory", menuKey: "inventory" },
  { to: "/purchasing", label: "Pembelian", icon: ShoppingCart, testId: "nav-purchasing", menuKey: "pembelian" },
  { to: "/sales", label: "Penjualan", icon: ShoppingBag, testId: "nav-sales", menuKey: "penjualan" },
  { to: "/laporan-penjualan", label: "Laporan Penjualan", icon: BarChart3, testId: "nav-sales-report", menuKey: "laporan_penjualan" },
  { to: "/laporan-rincian-shopee", label: "Rincian Shopee", icon: Store, testId: "nav-shopee-rincian", menuKey: "laporan_penjualan" },
  { to: "/cashbook", label: "Kas Operasional", icon: Wallet, testId: "nav-cashbook", menuKey: "kas_operasional" },
  { to: "/reports", label: "Laba/Rugi", icon: TrendingUp, testId: "nav-reports", menuKey: "laba_rugi" },
  { to: "/categories", label: "Master Kategori", icon: Tags, testId: "nav-categories", menuKey: "master_kategori" },
  { to: "/thr", label: "THR", icon: Gift, testId: "nav-thr", menuKey: "thr" },
  { to: "/leave", label: "Izin & Cuti", icon: CalendarDays, testId: "nav-leave", badgeKey: "pending", menuKey: "izin_cuti" },
  { to: "/users", label: "Kelola User", icon: UserCog, testId: "nav-users", menuKey: "kelola_user" },
  { to: "/settings", label: "Konfigurasi", icon: SettingsIcon, testId: "nav-settings", menuKey: "konfigurasi" },
  { to: "/backup", label: "Pusat Backup", icon: HardDrive, testId: "nav-backup", superAdminOnly: true },
];

const ROLE_BADGE = {
  super_admin: { label: "Super Admin", color: "text-[#002FA7]" },
  admin_privileged: { label: "Admin (Privilege)", color: "text-emerald-700" },
  hr_leave: { label: "Admin (Privilege)", color: "text-emerald-700" },
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pendingLeaves, setPendingLeaves] = useState(0);
  const [expiringContracts, setExpiringContracts] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  const role = user?.role;
  const navItems = ALL_NAV.filter((n) => {
    if (n.superAdminOnly) return role === "super_admin";
    if (!n.menuKey) return true;
    return hasMenuAccess(user, n.menuKey);
  });
  const canLoadPendingLeaves = navItems.some((item) => item.badgeKey === "pending");
  const canLoadExpiringContracts = navItems.some((item) => item.badgeKey === "contract_expiring");

  const loadStats = useCallback(async () => {
    // Leave stats
    if (canLoadPendingLeaves) {
      try {
        const { data } = await api.get("/leave/stats");
        setPendingLeaves(data.pending || 0);
      } catch {
        // ignore
      }
    }
    // Contract expiring (only for super_admin)
    if (canLoadExpiringContracts) {
      try {
        const { data } = await api.get("/contracts/expiring?days=30");
        setExpiringContracts(data.count || 0);
      } catch {
        // ignore
      }
    }
  }, [canLoadExpiringContracts, canLoadPendingLeaves]);

  useEffect(() => {
    loadStats();
    const t = setInterval(loadStats, 60000);
    return () => clearInterval(t);
  }, [loadStats]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const roleInfo = ROLE_BADGE[role];

  const SidebarContent = (
    <>
      <div className="px-5 py-6 border-b border-zinc-200 flex items-center justify-between shrink-0">
        <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-[#002FA7] flex items-center justify-center">
            <Square className="w-4 h-4 text-white" strokeWidth={2.5} fill="white" />
          </div>
          <div>
            <div className="font-heading font-bold text-zinc-900 leading-none tracking-tight">HRIS</div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-1">Human Resource Info System</div>
          </div>
        </Link>
        <button
          data-testid="close-mobile-menu"
          onClick={() => setMobileOpen(false)}
          className="md:hidden p-1.5 hover:bg-zinc-100"
          aria-label="Close menu"
        >
          <XIcon className="w-4 h-4" />
        </button>
      </div>

      <nav className="min-h-0 flex-1 py-4 overflow-y-auto overscroll-contain">
        {navItems.map((item) => {
          const Icon = item.icon;
          const badgeCount =
            item.badgeKey === "pending"
              ? pendingLeaves
              : item.badgeKey === "contract_expiring"
              ? expiringContracts
              : 0;
          const badgeColor = item.badgeKey === "contract_expiring" ? "bg-amber-600" : "bg-rose-600";
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={item.testId}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-2.5 text-sm font-medium border-l-2 transition-colors ${
                  isActive
                    ? "border-[#002FA7] bg-zinc-50 text-zinc-900"
                    : "border-transparent text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                }`
              }
            >
              <Icon className="w-4 h-4" strokeWidth={2} />
              <span className="flex-1">{item.label}</span>
              {badgeCount > 0 && (
                <span
                  data-testid={`badge-${item.testId}`}
                  className={`${badgeColor} text-white text-[10px] font-bold px-1.5 py-0.5 min-w-[18px] text-center leading-none rounded-sm`}
                >
                  {badgeCount}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="shrink-0 border-t border-zinc-200 bg-white p-4">
        <div className="text-[11px] uppercase tracking-widest text-zinc-400 font-semibold">Signed in</div>
        <div className="mt-1 text-sm text-zinc-900 font-medium truncate">{user?.name || "Admin"}</div>
        <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
        {roleInfo && (
          <div
            data-testid="user-role-badge"
            className={`mt-1.5 text-[10px] uppercase tracking-widest font-bold ${roleInfo.color}`}
          >
            {roleInfo.label}
          </div>
        )}
        <button
          data-testid="logout-button"
          onClick={handleLogout}
          className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-zinc-300 hover:bg-zinc-900 hover:text-white text-zinc-900 px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors"
        >
          <LogOut className="w-3.5 h-3.5" /> Keluar
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-zinc-50">
      {/* Mobile top bar (visible < md) */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 bg-white border-b border-zinc-200 flex items-center justify-between px-4 py-3 no-print">
        <button
          data-testid="open-mobile-menu"
          onClick={() => setMobileOpen(true)}
          className="p-1.5 -ml-1.5 hover:bg-zinc-100"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5 text-zinc-900" />
        </button>
        <Link to="/" className="flex items-center gap-2">
          <div className="w-6 h-6 bg-[#002FA7] flex items-center justify-center">
            <Square className="w-3 h-3 text-white" strokeWidth={2.5} fill="white" />
          </div>
          <div className="font-heading font-bold text-sm text-zinc-900 tracking-tight">HRIS</div>
        </Link>
        <div className="w-8" />{/* spacer to balance */}
      </div>

      {/* Desktop Sidebar (visible >= md) */}
      <aside className="hidden md:flex sticky top-0 h-screen w-60 bg-white border-r border-zinc-200 flex-col overflow-hidden no-print shrink-0 self-start">
        {SidebarContent}
      </aside>

      {/* Mobile Sidebar Drawer */}
      {mobileOpen && (
        <>
          <div
            onClick={() => setMobileOpen(false)}
            className="md:hidden fixed inset-0 z-40 bg-zinc-900/50 no-print"
          />
          <aside className="md:hidden fixed top-0 left-0 bottom-0 z-50 w-64 bg-white border-r border-zinc-200 flex flex-col no-print shadow-2xl">
            {SidebarContent}
          </aside>
        </>
      )}

      {/* Main */}
      <main className="flex-1 min-w-0 pt-14 md:pt-0">
        <Outlet />
      </main>
    </div>
  );
}
